import { useCallback, useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ShopError, api, type PickupPoint } from '../api';

/**
 * Vyber vydejniho mista.
 *
 * Mapa a seznam ukazuji tataz mista a jsou rovnocenne: kdyz se dlazdice
 * mapy nenactou (offline, blokovany tile server) nebo uzivatel pouziva
 * ctecku obrazovky, seznam funguje sam o sobe.
 */

interface Props {
  readonly carrier: string;
  readonly selected: PickupPoint | null;
  readonly onSelect: (point: PickupPoint) => void;
}

/** Stred Ceska – vychozi pohled, nez zname polohu zakaznika. */
const DEFAULT_CENTER: [number, number] = [49.8, 15.47];
const DEFAULT_ZOOM = 7;

export function PickupPointPicker({ carrier, selected, onSelect }: Props) {
  const [points, setPoints] = useState<readonly PickupPoint[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [locating, setLocating] = useState(false);
  const [tilesFailed, setTilesFailed] = useState(false);

  const mapRef = useRef<L.Map | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<L.LayerGroup | null>(null);
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const result = await api.pickupPoints(carrier, {
        query,
        ...(location === null ? {} : { lat: location.lat, lng: location.lng }),
      });
      if (id !== requestId.current) return;
      setPoints(result.points);
      setError(null);
    } catch (caught) {
      if (id !== requestId.current) return;
      setError(caught instanceof ShopError ? caught.message : 'Výdejní místa se nepodařilo načíst.');
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [carrier, query, location]);

  useEffect(() => {
    const timer = setTimeout(() => void load(), query === '' ? 0 : 250);
    return () => clearTimeout(timer);
  }, [load, query]);

  // Mapa se zaklada jednou; prekresluji se jen znacky.
  useEffect(() => {
    if (containerRef.current === null || mapRef.current !== null) return;

    const map = L.map(containerRef.current, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      scrollWheelZoom: false,
    });

    const tiles = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© přispěvatelé OpenStreetMap',
      maxZoom: 19,
    });
    // Podklad mapy je z ciziho serveru. Kdyz se nenacte, znacky i seznam
    // funguji dal – jen o tom dame vedet, at uzivatel necuma na sedou plochu.
    tiles.on('tileerror', () => setTilesFailed(true));
    tiles.addTo(map);

    markersRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current = null;
    };
  }, []);

  // Překreslení značek při každé změně výsledků.
  useEffect(() => {
    const map = mapRef.current;
    const markers = markersRef.current;
    if (map === null || markers === null) return;

    // React ve StrictMode effekty dvakrat spousti i uklizi. Kdyz uz je
    // kontejner odpojeny, Leaflet by pri prekreslovani sahnul na vnitrni
    // stav, ktery uz neexistuje.
    if (!map.getContainer().isConnected) return;

    markers.clearLayers();
    const bounds: [number, number][] = [];

    for (const point of points) {
      if (point.latitude === null || point.longitude === null) continue;
      const isSelected = selected?.code === point.code;

      L.circleMarker([point.latitude, point.longitude], {
        radius: isSelected ? 11 : 8,
        color: isSelected ? '#1f6f4a' : '#3d6b8a',
        fillColor: isSelected ? '#1f6f4a' : '#5b8fb0',
        fillOpacity: 0.9,
        weight: 2,
      })
        .bindPopup(
          `<strong>${escapeHtml(point.name)}</strong><br>${escapeHtml(point.street)}<br>${escapeHtml(point.postalCode)} ${escapeHtml(point.city)}`,
        )
        .on('click', () => onSelect(point))
        .addTo(markers);

      bounds.push([point.latitude, point.longitude]);
    }

    if (bounds.length > 0) {
      // Bez animace: animovany presun na odpojene mape konci vyjimkou.
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 14, animate: false });
    }
  }, [points, selected, onSelect]);

  function locate() {
    if (navigator.geolocation === undefined) {
      setError('Prohlížeč neumí zjistit polohu. Zkus hledat podle města nebo PSČ.');
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
        setLocating(false);
      },
      () => {
        // Odmitnuti polohy neni chyba – jen se nabidne hledani.
        setError('Polohu se nepodařilo zjistit. Zkus hledat podle města nebo PSČ.');
        setLocating(false);
      },
      { timeout: 8_000 },
    );
  }

  return (
    <div className="picker">
      <div className="picker-tools">
        <input
          type="search"
          className="search"
          placeholder="Město, PSČ nebo název místa…"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          aria-label="Hledat výdejní místo"
        />
        <button type="button" className="button ghost" onClick={locate} disabled={locating}>
          {locating ? 'Zjišťuji…' : 'Nejbližší ke mně'}
        </button>
      </div>

      {error !== null && <p className="field-error">{error}</p>}

      <div className="picker-body">
        <div className="picker-map-wrap">
          <div className="picker-map" ref={containerRef} role="application" aria-label="Mapa výdejních míst" />
          {tilesFailed && (
            <p className="picker-map-note muted small">
              Podklad mapy se nenačetl. Výdejní místo vyber ze seznamu vpravo.
            </p>
          )}
        </div>

        <ul className="picker-list">
          {points.map((point) => (
            <li key={`${point.carrier}-${point.code}`}>
              <button
                type="button"
                className={selected?.code === point.code ? 'picker-item selected' : 'picker-item'}
                onClick={() => {
                  onSelect(point);
                  if (point.latitude !== null && point.longitude !== null) {
                    mapRef.current?.setView([point.latitude, point.longitude], 15, {
                      animate: false,
                    });
                  }
                }}
              >
                <strong>{point.name}</strong>
                <span className="muted small">
                  {point.street}, {point.postalCode} {point.city}
                </span>
                {point.distanceKm !== undefined && (
                  <span className="muted small">{point.distanceKm} km od tebe</span>
                )}
              </button>
            </li>
          ))}

          {points.length === 0 && !loading && (
            <li className="muted small picker-empty">
              {query === '' ? 'Zatím žádná výdejní místa.' : 'Nic jsme nenašli.'}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
}

/** Popup v Leafletu prijima HTML – nazev mista je proto potreba escapovat. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
